import { Button,Skeleton } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useQueries } from '@tanstack/react-query';
import MUIDataTable,{ MUIDataTableOptions } from 'mui-datatables';
import { useContext,useEffect,useState } from 'react';
import { useTranslation } from "react-i18next";
import { FaNetworkWired } from "react-icons/fa";
import { RiPencilFill } from 'react-icons/ri';
import { UserContext } from '../../context/UserContext';
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import Clients from "../../service/Clients";
import { ClientType } from "../../types/clientes/types";
import CustomerTypeAssoci from "../PlacesComp/Modal/Associacoes/CustomerTypeAssoci";
import { getMuiTheme,tableOptions } from "../TableHelpers/options";
import { StyledDiv } from '../TableHelpers/TableStyle';
import ChangeCustomer from "./Modal/ChangeCustomer";

const customerService = new Clients();

export default function RegisteredCustomers() {
  const { hierarchyLabel } = useContext(UserContext);
  const [selectedCustomer, setSelectedCustomer] = useState<ClientType | null>(null);
  const [modalAssoci, setModalAssoci] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation('translation');

  const results = useQueries({
    queries: [
      {
        queryKey: ['GET_CUSTOMERS'],
        queryFn: async () => {
          try {
            const token = await AttToken();
            if (!token) throw new Error('Token not found');
            const body = { ...token, empresa: token.userEmpresa };
            const result: { clientList: ClientType[] } = await customerService.getAllClients(body, true);
            if (!result || !result.clientList) throw new Error('No client list found');
            return result.clientList;
          } catch (error) {
            console.error('Error fetching customers:', error);
          }
        },
        refetchOnMount: 'always',
        retry: 0,
        refetchOnWindowFocus: false
      },
      {
        queryKey: ['GET_TAGS'],
        queryFn: async () => {
          try {
            const token = await AttToken();
            if (!token) throw new Error('Token not found');
            const response: { tags: { id: string, name: string }[] } = await customerService.getClientTag(token);
            if (!response || !response.tags) throw new Error('No tags found');
            return response.tags;
          } catch (error) {
            console.error('Error fetching tags:', error);
          }
        }
      }
    ]
  });

  const isLoading = results?.some((result) => result.isLoading);
  const isFetching = results?.some((result) => result.isFetching);
  const customersLoaded = results[0]?.isSuccess;
  const customersError = results[0]?.isError;
  const tagsError = results[1]?.isError;

  useEffect(() => {
    if (customersLoaded) trackEventMatomo('gestão de clientes', 'sucesso', 'carregamento', 'clientes carregados');
    if (customersError) trackEventMatomo('gestão de clientes', 'erro', 'carregamento', 'falha ao carregar clientes');
    if (tagsError) trackEventMatomo('gestão de clientes', 'erro', 'carregamento', 'falha ao carregar tags de clientes');
  }, [customersError, customersLoaded, tagsError]);

  const columns = [
    {
      name: "nome",
      label: hierarchyLabel?.cliente,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'contato',
      label: t('tel'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'responsavel',
      label: t('customerPage.responsible'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "status",
      label: t('status'),
      options: {
        customBodyRender: (value: string) => (
          value.toLocaleLowerCase() === 'ativo' ? <span className="tag is-primary">{t('active')}</span> : <span className="tag is-danger">{t('inactive')}</span>
        ),
        filter: false,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: t('edit'),
      options: {
        customBodyRender: (value: string) => {
          const cliente = Array.from(results?.[0]?.data as ClientType[] || [])?.find((customer) => customer?.id === value);
          return (
            <Button
              endIcon={<RiPencilFill />}
              className="button"
              onClick={() => {
                trackEventMatomo('lista de cliente', 'click', 'td', 'abre modal');
                if (cliente) {
                  setSelectedCustomer(cliente);
                  setOpenModal(true);
                }
              }}>
              {t('edit')}
            </Button>
          );
        },
        filter: false,
        sort: false,
      }
    },
    {
      name: "id",
      label: `${t('notifications.ticket.tipoServico')}`,
      options: {
        customBodyRender: (value: string) => {
          const cliente = Array.from(results?.[0]?.data as ClientType[] || [])?.find((customer) => customer?.id === value);
          return (
            <Button
              endIcon={<FaNetworkWired />}
              className="button is-small is-light"
              onClick={() => {
                trackEventMatomo('lista de cliente', 'click', 'td', 'abre modal');
                if (cliente) {
                  setSelectedCustomer(cliente);
                  setModalAssoci(true);
                }
              }}>
              {t('notifications.ticket.AssociarTiposServico')}
            </Button>
          );
        },
        filter: false,
        sort: false,
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  const onLoading = isLoading || isFetching;

  return (
    onLoading ? (<Skeleton variant='rounded' width='100%' height={400} />) : (
      <>
        {openModal &&
          <ChangeCustomer
            openModal={openModal}
            setOpenModal={setOpenModal}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            customerTags={results[1].data as { id: string, name: string }[] || []}
          />}
        {modalAssoci &&
          (<CustomerTypeAssoci
            open={modalAssoci}
            setOpen={setModalAssoci}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
          />)}
        <StyledDiv>
          <ThemeProvider theme={getMuiTheme()}>
            <MUIDataTable
              title={`${hierarchyLabel?.cliente} ${t('customerPage.registered')}`}
              data={results[0]?.data as ClientType[] || []}
              columns={columns}
              options={{ ...tableOptions, ...translatedTextLabels }}
            />
          </ThemeProvider>
        </StyledDiv>
      </>
    )
  );
}
